/**
 * Video Encryption Service Factory
 * 
 * Auto-selects the appropriate video encryption service based on browser capabilities:
 * - Web Crypto API available: Use WebCryptoVideoEncryptionService (faster, native)
 * - Web Crypto API unavailable: Use CryptoJsVideoEncryptionService (fallback for old browsers)
 */

import type { VideoDecryptionEntity, VideoDecryptionKeys, VideoSecretNamePrefixes } from '@/domain/entities/video/videoDecryptionEntity';
import type { MediaCdnTokensDto } from '@/data/dtos/encryption/mediaCdnTokensDto';
import { appLogger } from '@/domain/services/analytics/appLogger';

interface IVideoEncryptionService {
  processVideoEncryption(
    videoId: string,
    uid: string,
    iat: number,
    cdnTokens: MediaCdnTokensDto,
    secretNamePrefixes?: VideoSecretNamePrefixes
  ): Promise<VideoDecryptionEntity>;

  getVideoDecryption(videoId: string): VideoDecryptionEntity | null;
  clearVideoDecryption(videoId: string): void;
  clearAllDecryption(): void;

  decryptVideoChunk(
    encryptedData: ArrayBuffer,
    keys: VideoDecryptionKeys
  ): Promise<ArrayBuffer>;
}

class VideoEncryptionServiceFactory {
  private static instance: VideoEncryptionServiceFactory;
  private service: IVideoEncryptionService | null = null;
  private serviceType: 'webcrypto' | 'cryptojs' | null = null;

  private constructor() {
    this.selectService();
  }

  public static getInstance(): VideoEncryptionServiceFactory {
    if (!VideoEncryptionServiceFactory.instance) {
      VideoEncryptionServiceFactory.instance = new VideoEncryptionServiceFactory();
    }
    return VideoEncryptionServiceFactory.instance;
  }

  private async checkWebCryptoSupport(): Promise<boolean> {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      return false;
    }

    try {
      const testData = new Uint8Array([1, 2, 3, 4]);
      await crypto.subtle.digest('SHA-256', testData);
      return true;
    } catch {
      return false;
    }
  }

  private async selectService(): Promise<void> {
    const hasWebCrypto = await this.checkWebCryptoSupport();

    if (hasWebCrypto) {
      const { WebCryptoVideoEncryptionService } = await import('./webCryptoVideoEncryptionService');
      this.service = WebCryptoVideoEncryptionService.getInstance() as IVideoEncryptionService;
      this.serviceType = 'webcrypto';
    } else {
      const { CryptoJsVideoEncryptionService } = await import('./cryptoJsVideoEncryptionService');
      this.service = CryptoJsVideoEncryptionService.getInstance() as IVideoEncryptionService;
      this.serviceType = 'cryptojs';

      appLogger.warning('Web Crypto API not available, using CryptoJS fallback', 'VideoEncryptionServiceFactory.selectService', { context: {
        serviceType: 'cryptojs',
        userAgent: navigator.userAgent,
        platform: navigator.platform
      } });
    }
  }

  private async getService(): Promise<IVideoEncryptionService> {
    while (this.service === null) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    return this.service;
  }

  public getServiceType(): 'webcrypto' | 'cryptojs' | null {
    return this.serviceType;
  }

  public async processVideoEncryption(
    videoId: string,
    uid: string,
    iat: number,
    cdnTokens: MediaCdnTokensDto,
    secretNamePrefixes?: VideoSecretNamePrefixes
  ): Promise<VideoDecryptionEntity> {
    const service = await this.getService();
    return service.processVideoEncryption(videoId, uid, iat, cdnTokens, secretNamePrefixes);
  }

  public async getVideoDecryption(videoId: string): Promise<VideoDecryptionEntity | null> {
    const service = await this.getService();
    return service.getVideoDecryption(videoId);
  }

  public async clearVideoDecryption(videoId: string): Promise<void> {
    const service = await this.getService();
    service.clearVideoDecryption(videoId);
  }

  public async clearAllDecryption(): Promise<void> {
    const service = await this.getService();
    service.clearAllDecryption();
  }

  public async decryptVideoChunk(
    encryptedData: ArrayBuffer,
    keys: VideoDecryptionKeys
  ): Promise<ArrayBuffer> {
    const service = await this.getService();
    return service.decryptVideoChunk(encryptedData, keys);
  }
}

export const videoEncryptionService = VideoEncryptionServiceFactory.getInstance();
